/**
 * Filter helpers for the riu_admin assessment templates page. The KPI
 * cascade tag filter lives on the KPI picker modal (KpiPickerModal.js)
 * because templates themselves don't carry tags — only KPIs do.
 */

function humaniseStatus(value) {
  if (value == null || value === "") return String(value ?? "");
  return String(value)
    .toLowerCase()
    .replace(/_+/g, " ")
    .replace(/^\s*([a-z])/, (m, c) => c.toUpperCase());
}

/**
 * Collect every distinct value seen at `row[field]` and surface them as
 * `[{ value, label }, ...]` so the filter only ever offers options that
 * actually appear in the data on the page.
 */
export function buildStatusOptions(rows, field = "status_key") {
  const seen = new Set();
  for (const r of rows || []) {
    const v = r?.[field];
    if (v == null || v === "") continue;
    seen.add(String(v));
  }
  return Array.from(seen)
    .sort()
    .map((value) => ({ value, label: humaniseStatus(value) }));
}

/**
 * Build `[{ value, label }, ...]` where the label resolves via a lookup
 * map (e.g. industry_id → industry name). Used by the industry and
 * sub-industry filters on the templates page.
 */
export function buildLookupOptions(rows, idField, labelMap = {}) {
  const seen = new Set();
  for (const r of rows || []) {
    const v = r?.[idField];
    if (v == null || v === "") continue;
    seen.add(String(v));
  }
  return Array.from(seen)
    .map((value) => ({
      value,
      label: labelMap[value] || labelMap[Number(value)] || `#${value}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function rowMatchesSearch(row, search) {
  const q = (search || "").trim().toLowerCase();
  if (!q) return true;
  const haystacks = [row?.title, row?.description];
  return haystacks.filter(Boolean).some((s) =>
    String(s).toLowerCase().includes(q)
  );
}

function rowMatchesIn(row, values, field) {
  if (!values?.length) return true;
  const v = row?.[field];
  if (v == null) return false;
  return values.includes(String(v));
}

function withinRange(dateStr, from, to) {
  if (!dateStr) return false;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return false;
  if (from) {
    const fromT = new Date(from).getTime();
    if (Number.isFinite(fromT) && t < fromT) return false;
  }
  if (to) {
    const toT = new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1;
    if (Number.isFinite(toT) && t > toT) return false;
  }
  return true;
}

function rowMatchesDate(row, dateRange, dateField) {
  if (!dateRange?.from && !dateRange?.to) return true;
  if (!dateField) return true;
  return withinRange(row?.[dateField], dateRange.from, dateRange.to);
}

/**
 * Apply the standard list-page filter set plus any per-page multi-value
 * filters passed via `lookups: [{ values: [...], field: "industry" }]`.
 * Pure — returns a new array; never mutates inputs.
 */
export function applyAssessmentFilters(rows, opts = {}) {
  const {
    search = "",
    statusFilter = [],
    statusField = "status_key",
    dateRange = { from: "", to: "" },
    dateField = "updated_at",
    lookups = [],
  } = opts;
  return (rows || []).filter((r) => {
    if (!rowMatchesSearch(r, search)) return false;
    if (!rowMatchesIn(r, statusFilter, statusField)) return false;
    if (!rowMatchesDate(r, dateRange, dateField)) return false;
    for (const lk of lookups) {
      if (!rowMatchesIn(r, lk.values, lk.field)) return false;
    }
    return true;
  });
}
